import React, { useState } from 'react';
import axios from 'axios';
import { ShoppingBag, Minus, Plus, Trash2, Lock, Loader2 } from 'lucide-react';
import { PRODUCTS } from '../data/mock';
import { usePageMeta } from '../components/Layout';
import { Reveal, PageHero, CloseCTA } from '../components/Shared';
import { toast } from 'sonner';
import { Toaster } from '../components/ui/sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const CART_KEY = 'pitfit_cart_v2';

function loadCart() {
  try {
    const c = JSON.parse(localStorage.getItem(CART_KEY) || '{}');
    return typeof c === 'object' && !Array.isArray(c) ? c : {};
  } catch {
    return {};
  }
}

export default function Shop() {
  usePageMeta(
    'Shop — PITFIT Merch',
    'PITFIT merch: training tees, hoodies, steel bottles and gym duffels — garage-tested gear with secure online checkout.'
  );

  const [cart, setCart] = useState(loadCart);
  const [busy, setBusy] = useState(false);

  const setQty = (id, qty) => {
    const next = { ...cart };
    if (qty <= 0) delete next[id];
    else next[id] = Math.min(qty, 20);
    setCart(next);
    localStorage.setItem(CART_KEY, JSON.stringify(next));
  };

  const addToCart = (p) => {
    setQty(p.id, (cart[p.id] || 0) + 1);
    toast.success(`${p.name} added to bag`, { description: `$${p.price} · secure Stripe checkout below` });
  };

  const entries = Object.entries(cart)
    .map(([id, qty]) => ({ product: PRODUCTS.find((p) => p.id === id), qty }))
    .filter((e) => e.product);
  const itemCount = entries.reduce((s, e) => s + e.qty, 0);
  const subtotal = entries.reduce((s, e) => s + e.product.price * e.qty, 0);

  const checkout = async () => {
    if (!entries.length || busy) return;
    setBusy(true);
    try {
      const items = entries.map((e) => ({ lookup_key: `merch_${e.product.id}`, quantity: e.qty }));
      const res = await axios.post(`${API}/payments/checkout`, { items, origin_url: window.location.origin });
      window.location.href = res.data.checkout_url;
    } catch {
      toast.error('Checkout failed — please try again.');
      setBusy(false);
    }
  };

  return (
    <>
      <Toaster position="bottom-right" theme="dark" />
      <PageHero
        tag="Merch"
        title="Gear that earned its keep."
        sub="A lean lineup, garage-tested by the 6 AM crew. If it can survive the PIT, it can survive your week."
      />

      <section className="section-pad !pt-8">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="flex items-center justify-between mb-8">
            <p className="font-mono-pf text-[12px] uppercase tracking-[0.12em] text-muted-pf">{PRODUCTS.length} products</p>
            <p className="font-mono-pf text-[12px] uppercase tracking-[0.12em] flex items-center gap-2" style={{ color: 'var(--pf-brand2)' }} data-testid="cart-count">
              <ShoppingBag size={14} /> {itemCount} in bag
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {PRODUCTS.map((p, i) => (
              <Reveal key={p.id} delay={i * 70}>
                <div className="glass-card glass-card-hover overflow-hidden flex flex-col h-full" data-testid={`shop-product-${p.id}`}>
                  <img src={p.img} alt={p.name} className="w-full h-64 object-cover" loading="lazy" />
                  <div className="p-6 flex flex-col flex-1">
                    <h3 className="font-display font-semibold text-white text-lg">{p.name}</h3>
                    <p className="text-muted-pf text-[14px] mt-1.5 flex-1">{p.desc}</p>
                    <div className="flex items-center justify-between mt-5">
                      <span className="font-display font-bold text-2xl text-white">${p.price}</span>
                      {cart[p.id] ? (
                        <div className="flex items-center gap-2">
                          <button onClick={() => setQty(p.id, cart[p.id] - 1)} aria-label={`Remove one ${p.name}`} className="w-9 h-9 rounded-lg border border-[rgba(148,170,220,0.3)] text-white flex items-center justify-center hover:border-[rgba(125,167,255,0.7)] transition-colors" data-testid={`qty-minus-${p.id}`}><Minus size={14} /></button>
                          <span className="font-mono-pf text-white w-6 text-center" data-testid={`qty-${p.id}`}>{cart[p.id]}</span>
                          <button onClick={() => setQty(p.id, cart[p.id] + 1)} aria-label={`Add one ${p.name}`} className="w-9 h-9 rounded-lg border border-[rgba(148,170,220,0.3)] text-white flex items-center justify-center hover:border-[rgba(125,167,255,0.7)] transition-colors" data-testid={`qty-plus-${p.id}`}><Plus size={14} /></button>
                        </div>
                      ) : (
                        <button onClick={() => addToCart(p)} className="btn-ghost !min-h-[44px] !py-2 !px-5 text-[14px]" data-testid={`add-to-bag-${p.id}`}>
                          Add to Bag
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          {entries.length > 0 && (
            <Reveal className="mt-10">
              <div className="glass-card p-7 md:p-8" style={{ borderColor: 'rgba(125,167,255,0.35)' }} data-testid="cart-summary">
                <p className="mono-label mb-5">Your Bag</p>
                <div className="space-y-4">
                  {entries.map(({ product, qty }) => (
                    <div key={product.id} className="flex items-center justify-between gap-4 border-b border-[rgba(148,170,220,0.1)] pb-4" data-testid={`cart-row-${product.id}`}>
                      <div className="flex items-center gap-4 min-w-0">
                        <img src={product.img} alt="" className="w-12 h-12 rounded-lg object-cover shrink-0" />
                        <div className="min-w-0">
                          <p className="text-white font-display font-semibold text-[15px] truncate">{product.name}</p>
                          <p className="text-muted-pf text-[13px]">${product.price} × {qty}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        <span className="font-display font-bold text-white">${product.price * qty}</span>
                        <button onClick={() => setQty(product.id, 0)} aria-label={`Remove ${product.name} from bag`} className="p-2 text-muted-pf hover:text-white transition-colors" data-testid={`cart-remove-${product.id}`}><Trash2 size={16} /></button>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mt-6">
                  <div>
                    <p className="font-mono-pf text-[11.5px] uppercase tracking-[0.12em] text-muted-pf">Subtotal · tax &amp; shipping at checkout</p>
                    <p className="font-display font-bold text-3xl text-white mt-1" data-testid="cart-subtotal">${subtotal}</p>
                  </div>
                  <button onClick={checkout} disabled={busy} className="btn-primary !min-h-[52px] disabled:opacity-60" data-testid="cart-checkout-btn">
                    {busy ? <><Loader2 size={17} className="animate-spin" /> Redirecting…</> : <><Lock size={16} /> Secure Checkout</>}
                  </button>
                </div>
              </div>
            </Reveal>
          )}

          <p className="font-mono-pf text-[12px] uppercase tracking-[0.12em] text-muted-pf mt-10 text-center flex items-center justify-center gap-2">
            <Lock size={12} /> Secure checkout powered by Stripe · ships in the US or pick up in studio
          </p>
        </div>
      </section>

      <CloseCTA title="The best merch is the sweat." sub="Earn the shirt first — your first class is $15." />
    </>
  );
}
